import { Button, DataList, Flex, IconButton, List, Stack, useDisclosure } from '@chakra-ui/react';
import { User } from '@/api/User';
import { Tooltip } from '@/components/ui/tooltip';
import { LuPencilLine, LuTrash } from 'react-icons/lu';
import { Select as ReactSelect } from 'chakra-react-select';
import { LabelAndValueOption } from '@/data/LabelAndValueOption';
import { PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger } from '@/components/ui/popover';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useEffect, useMemo, useState } from 'react';
import { userService } from '@/service/UserService';

type UserReportingSectionProps = {
    user: User | null;
    org: User[]; // all active, except the user
    setIsLoading: (loading: boolean) => void;
    canEdit?: boolean;
};

const UserReportingSection = ({
    user,
    org,
    setIsLoading,
    canEdit = true,
}: UserReportingSectionProps) => {
    const [manager, setManager] = useState<User | null>(null);
    const [reports, setReports] = useState<User[]>([]);
    const [selectedManager, setSelectedManager] = useState<LabelAndValueOption | null>(null);
    const [isManagerPopoverOpen, setIsManagerPopoverOpen] = useState(false);
    const { open: isDeleteManagerOpen, onOpen: onDeleteManagerOpen, onClose: onDeleteManagerClose } = useDisclosure();

    const managerOptions: LabelAndValueOption[] = useMemo(
        () => org.map(u => ({ label: `${u.firstName} ${u.lastName}`, value: u.id })),
        [org]
    );

    useEffect(() => {
        const userId = user?.id;
        if (userId === undefined) return;
        userService.listReports().then(r => {
            const reportIds = userService.toNumberArrayMap(r.reports).get(userId) ?? [];
            const reports = reportIds.map(id => org.find(u => u.id === id)).filter(u => u !== undefined) as User[];
            setReports(reports);

            const managerIds = userService.toNumberArrayMap(r.reportingTo).get(userId) ?? [];
            const managerId = managerIds?.[0];
            setManager(managerId !== undefined ? org.find(u => u.id === managerId) ?? null : null);
        })
    }, [user, org]);

    const onDeleteManager = async () => {
        if (!user || !manager) return;
        setIsLoading(true);
        try {
            await userService.deleteReport(manager.id, user.id);
            setManager(null);
        } finally {
            setIsLoading(false);
        }
    };

    const onEditManager = () => {
        setSelectedManager(manager ? { label: `${manager.firstName} ${manager.lastName}`, value: manager.id } : null);
        setIsManagerPopoverOpen(true);
    };

    const onCancelEditManager = () => {
        setIsManagerPopoverOpen(false);
    };

    const onSaveManager = async () => {
        if (!user || !selectedManager) return;
        setIsLoading(true);
        try {
            await userService.addReport(selectedManager.value, user.id);
            setManager(org.find(u => u.id === selectedManager.value) ?? null);
            setIsManagerPopoverOpen(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <DataList.Root orientation={{ base: 'vertical', sm: 'horizontal' }} gap={{ base: '4', sm: '0' }}>
                <DataList.Item key='manager' flexDirection={{ sm: 'row' }}>
                    <DataList.ItemLabel>Berichtet An</DataList.ItemLabel>
                    <DataList.ItemValue>{manager ? <div>{manager.firstName} {manager.lastName}</div> : '-'}</DataList.ItemValue>
                    {canEdit && (
                        <Flex>
                            <PopoverRoot
                                open={isManagerPopoverOpen}
                                onOpenChange={(e) => setIsManagerPopoverOpen(e.open)}
                                positioning={{ placement: 'bottom-start' }}
                            >
                                <PopoverTrigger asChild>
                                    <span>
                                        <Tooltip content='Bearbeiten'>
                                            <IconButton aria-label="Bearbeiten" colorPalette="blue" variant="ghost" size="xs" onClick={onEditManager}>
                                                <LuPencilLine />
                                            </IconButton>
                                        </Tooltip>
                                    </span>
                                </PopoverTrigger>
                                <PopoverContent w='20rem' maxW='calc(100vw - 2rem)'>
                                    <PopoverBody>
                                        <Stack gap='0.75rem'>
                                            <ReactSelect<LabelAndValueOption, false>
                                                isSearchable
                                                isClearable
                                                placeholder='Manager auswählen'
                                                options={managerOptions}
                                                value={selectedManager}
                                                onChange={(val) => setSelectedManager(val ?? null)}
                                            />
                                            <Flex justify='flex-end' gap={2} flexWrap='wrap'>
                                                <Button size='sm' variant='outline' colorPalette='gray' onClick={onCancelEditManager}>Cancel</Button>
                                                <Button size='sm' colorPalette='blue' onClick={() => { void onSaveManager(); }} disabled={!selectedManager}>
                                                    Save
                                                </Button>
                                            </Flex>
                                        </Stack>
                                    </PopoverBody>
                                </PopoverContent>
                            </PopoverRoot>
                            <Tooltip content='Löschen'>
                                <IconButton aria-label="Löschen" colorPalette="red" variant="ghost" size="xs" onClick={onDeleteManagerOpen} disabled={!manager}>
                                    <LuTrash />
                                </IconButton>
                            </Tooltip>
                        </Flex>
                    )}
                </DataList.Item>
                <DataList.Item key='reports' flexDirection={{ sm: 'row' }}>
                    <DataList.ItemLabel>Direkte Berichte</DataList.ItemLabel>
                    <DataList.ItemValue>
                        <List.Root>
                            {reports.map(r => <List.Item key={r.id}>{r.firstName} {r.lastName}</List.Item>)}
                        </List.Root>
                    </DataList.ItemValue>
                </DataList.Item>
            </DataList.Root>
            {canEdit && (
                <ConfirmDialog
                    heading={`${manager?.firstName ?? ''} ${manager?.lastName ?? ''} löschen`}
                    isOpen={isDeleteManagerOpen}
                    onClose={onDeleteManagerClose}
                    onConfirm={onDeleteManager}
                />
            )}
        </>
    );
};

export default UserReportingSection;
